import { Button } from "antd";
import {
  ArrowRightOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  FileDoneOutlined,
  FileSearchOutlined,
  LockOutlined,
  RadarChartOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import productShowcase from "../assets/image/home-product-showcase.png";
import productShowcaseLight from "../assets/image/home-product-showcase-light.png";
import "../style/public.scss";

const features = [
  {
    icon: <FileDoneOutlined />,
    title: "Tailored application documents",
    text: "Turn a job description into a focused resume draft and cover letter built around the role.",
  },
  {
    icon: <FileSearchOutlined />,
    title: "Opportunity analysis",
    text: "Review roles, filter opportunities, and keep the information needed for each application together.",
  },
  {
    icon: <SolutionOutlined />,
    title: "Application pipeline",
    text: "Track every bid from first review to interview, outcome, and follow-up without losing context.",
  },
  {
    icon: <CalendarOutlined />,
    title: "Interview scheduling",
    text: "Organize calls and interviews across calendar, timeline, and meeting views for a clearer week.",
  },
  {
    icon: <BarChartOutlined />,
    title: "Practical analytics",
    text: "Understand application volume, interview activity, outcomes, and team contribution at a glance.",
  },
  {
    icon: <TeamOutlined />,
    title: "Team-ready workspace",
    text: "Coordinate recruiters and applicants with permissions, shared visibility, and consistent workflows.",
  },
];

const Home = ({ title }) => {
  document.title = title;
  const authenticated = useSelector((state) => state.auth.isAuthenticated);
  const darkMode = useSelector((state) => state.themeMode.themeMode);
  const primaryPath = authenticated ? "/dashboard" : "/register";
  const primaryLabel = authenticated ? "Open dashboard" : "Start building";

  return (
    <div className="public-page public-home-page">
      <section className="home-hero">
        <div className="home-hero-copy">
          <span className="public-eyebrow">
            <ThunderboltOutlined /> AI-powered application workspace
          </span>
          <h1>From job description to interview, all in one workspace.</h1>
          <p>
            Resume Builder helps individuals and recruiting teams create
            tailored application documents, organize opportunities, manage
            interviews, and learn from every result.
          </p>
          <div className="home-actions">
            <Link to={primaryPath}>
              <Button type="primary" size="large">
                {primaryLabel} <ArrowRightOutlined />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="large">Explore pricing</Button>
            </Link>
          </div>
          <div className="home-trust-list" aria-label="Product highlights">
            <span>
              <CheckCircleFilled /> Role-specific documents
            </span>
            <span>
              <CheckCircleFilled /> Clear application tracking
            </span>
            <span>
              <CheckCircleFilled /> Team analytics
            </span>
          </div>
        </div>

        <div className="home-hero-visual">
          <div className="home-visual-glow" />
          <img
            src={darkMode ? productShowcase : productShowcaseLight}
            alt="An anonymized Resume Builder dashboard showing applications, interviews, and analytics"
          />
          <div className="home-floating-note home-floating-note--top">
            <RadarChartOutlined />
            <span>
              <strong>One clear view</strong>Applications, calls, and outcomes
            </span>
          </div>
          <div className="home-floating-note home-floating-note--bottom">
            <CheckCircleFilled />
            <span>
              <strong>Ready to apply</strong>Keep every next step visible
            </span>
          </div>
        </div>
      </section>

      <section className="home-intro" aria-labelledby="home-intro-title">
        <div>
          <span className="home-section-kicker">
            Built for the whole journey
          </span>
          <h2 id="home-intro-title">More than a resume generator.</h2>
        </div>
        <p>
          A strong document is only the beginning. Resume Builder connects
          document creation with the daily work that follows: finding roles,
          tracking progress, preparing for interviews, coordinating a team, and
          understanding what is working.
        </p>
      </section>

      <section className="home-workflow" aria-label="How Resume Builder works">
        {[
          [
            "01",
            "Add the opportunity",
            "Paste the job URL and description so every application starts with the right context.",
          ],
          [
            "02",
            "Create a tailored draft",
            "Generate role-focused resume content, then review and refine it before use.",
          ],
          [
            "03",
            "Track every next step",
            "Move from application to call and interview while keeping schedules and outcomes organized.",
          ],
        ].map(([number, heading, text]) => (
          <article key={number}>
            <span>{number}</span>
            <div>
              <h3>{heading}</h3>
              <p>{text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="home-features" aria-labelledby="home-features-title">
        <div className="home-section-heading">
          <span className="home-section-kicker">Everything connected</span>
          <h2 id="home-features-title">
            A focused toolkit for better application work.
          </h2>
          <p>
            Use one system from first opportunity review through reporting and
            follow-up.
          </p>
        </div>
        <div className="home-feature-grid">
          {features.map(({ icon, title: featureTitle, text }) => (
            <article className="home-feature-card" key={featureTitle}>
              <span className="home-feature-icon">{icon}</span>
              <h3>{featureTitle}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-insight-section">
        <div className="home-insight-copy">
          <span className="home-section-kicker">See the bigger picture</span>
          <h2>Turn activity into decisions your team can use.</h2>
          <p>
            Weekly dashboards bring applications and interviews together so you
            can spot workload, momentum, bottlenecks, and outcomes without
            stitching together separate spreadsheets.
          </p>
          <ul>
            <li>
              <CheckCircleFilled /> Compare application and interview activity
            </li>
            <li>
              <CheckCircleFilled /> Review individual and team contribution
            </li>
            <li>
              <CheckCircleFilled /> Filter by period, status, role, and source
            </li>
          </ul>
        </div>
        <div className="home-insight-panel" aria-label="Analytics example">
          <div className="home-insight-panel-head">
            <span>Weekly progress</span>
            <b>Analytics</b>
          </div>
          <div className="home-insight-bars">
            {[58, 76, 48, 88, 68, 94, 73].map((height, index) => (
              <i key={index} style={{ "--bar-height": `${height}%` }} />
            ))}
          </div>
          <div className="home-insight-metrics">
            <div>
              <span>Applications</span>
              <strong>128</strong>
            </div>
            <div>
              <span>Interviews</span>
              <strong>32</strong>
            </div>
            <div>
              <span>In progress</span>
              <strong>24</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="home-security">
        <span className="home-security-icon">
          <SafetyCertificateOutlined />
        </span>
        <div>
          <span className="home-section-kicker">Organized by design</span>
          <h2>Your workspace, people, and preferences in the right place.</h2>
          <p>
            Profile templates, regional settings, balance activity, and
            role-based access controls help teams keep work consistent while
            limiting access to the pages each person needs.
          </p>
        </div>
        <div className="home-security-points">
          <span>
            <LockOutlined /> Role-based permissions
          </span>
          <span>
            <FileDoneOutlined /> Reusable document templates
          </span>
          <span>
            <TeamOutlined /> Shared team workflows
          </span>
        </div>
      </section>

      <section className="home-final-cta">
        <div>
          <span>Build. Apply. Track. Improve.</span>
          <h2>Bring your application workflow into focus.</h2>
          <p>
            Start with a tailored document and keep every opportunity moving.
          </p>
        </div>
        <div className="home-final-actions">
          <Link to={primaryPath}>
            <Button type="primary" size="large">
              {primaryLabel} <ArrowRightOutlined />
            </Button>
          </Link>
          {!authenticated && <Link to="/login">Sign in to your workspace</Link>}
        </div>
      </section>
    </div>
  );
};

export default Home;
